import { apiGet, apiPut } from '@/api/http'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface UserPreferences {
  userId: number
  bugTrackerEnabled: boolean
}

interface CachedPreferences {
  data: UserPreferences
  timestamp: number
  expiresAt: number
}

const CACHE_KEY = 'bdc-user-preferences'
const CACHE_TTL = 30 * 60 * 1000 // 30 minutes

export const useUserPreferencesStore = defineStore('userPreferences', () => {
  const preferences = ref<UserPreferences>({
    userId: 0,
    bugTrackerEnabled: false,
  })
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const lastFetchTime = ref<number | null>(null)

  /**
   * Charger les préférences depuis le cache localStorage
   */
  function loadFromCache(): UserPreferences | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (!cached) return null

      const cachedData: CachedPreferences = JSON.parse(cached)
      const now = Date.now()

      // Vérifier si le cache est valide
      if (now < cachedData.expiresAt) {
        //console.log('[BdC-UserPreferencesStore] Cache valide, utilisation des données en cache')
        return cachedData.data
      }

      // Cache expiré, le supprimer
      localStorage.removeItem(CACHE_KEY)
      return null
    } catch (err) {
      console.warn('[BdC-UserPreferencesStore] Erreur lecture cache:', err)
      localStorage.removeItem(CACHE_KEY)
      return null
    }
  }

  /**
   * Sauvegarder les préférences dans le cache localStorage
   */
  function saveToCache(data: UserPreferences): void {
    try {
      const cached: CachedPreferences = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + CACHE_TTL,
      }
      localStorage.setItem(CACHE_KEY, JSON.stringify(cached))
      //console.log('[BdC-UserPreferencesStore] Préférences sauvegardées dans le cache')
    } catch (err) {
      console.warn('[BdC-UserPreferencesStore] Erreur sauvegarde cache:', err)
    }
  }

  /**
   * Charger les préférences depuis l'API ou le cache
   * @param forceRefresh Force le rechargement depuis l'API même si le cache est valide
   */
  async function loadPreferences(forceRefresh = false): Promise<void> {
    // Vérifier le cache d'abord si pas de force refresh
    if (!forceRefresh) {
      const cached = loadFromCache()
      if (cached) {
        preferences.value = cached
        lastFetchTime.value = Date.now()
        //console.log('[BdC-UserPreferencesStore] Préférences chargées depuis le cache')
        return
      }
    }

    isLoading.value = true
    error.value = null
    try {
      // console.log("[BdC-UserPreferencesStore] Chargement préférences depuis l'API")
      const data = await apiGet<UserPreferences>('/users/me/preferences')
      preferences.value = data
      lastFetchTime.value = Date.now()
      saveToCache(data)
    } catch (err) {
      console.error('[BdC-UserPreferencesStore] Erreur chargement préférences:', err)
      error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des préférences'
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Mettre à jour les préférences
   */
  async function updatePreferences(newPreferences: Partial<UserPreferences>): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      //console.log('[BdC-UserPreferencesStore] Mise à jour préférences:', newPreferences)
      const data = await apiPut<UserPreferences>('/users/me/preferences', newPreferences)
      preferences.value = { ...preferences.value, ...data }
      lastFetchTime.value = Date.now()
      // Mettre à jour le cache après modification
      saveToCache(preferences.value)
      console.log('[BdC-UserPreferencesStore] Préférences mises à jour:', preferences.value)
    } catch (err) {
      console.error('[BdC-UserPreferencesStore] Erreur mise à jour préférences:', err)
      error.value =
        err instanceof Error ? err.message : 'Erreur lors de la mise à jour des préférences'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Invalider le cache (utile lors de la déconnexion)
   */
  function invalidateCache(): void {
    localStorage.removeItem(CACHE_KEY)
    lastFetchTime.value = null
    console.log('[BdC-UserPreferencesStore] Cache invalidé')
  }

  /**
   * Toggle bug tracker enabled
   */
  async function toggleBugTracker(): Promise<void> {
    await updatePreferences({
      bugTrackerEnabled: !preferences.value.bugTrackerEnabled,
    })
  }

  return {
    preferences,
    isLoading,
    error,
    lastFetchTime,
    loadPreferences,
    updatePreferences,
    toggleBugTracker,
    invalidateCache,
  }
})
